import { useCallback, useEffect, useRef } from 'react'

import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import {
  completeSeparation,
  fetchSeparationQuestions,
} from './separation.actions'
import { POLL_MAX_MS, POLL_START_MS } from './separation.constants'
import { markSeparationReleased } from './separation.slice'
import {
  selectSeparationCounts,
  selectSeparationDeferred,
  selectSeparationLoadFailed,
  selectSeparationQuestions,
  selectSeparationReleased,
  selectSeparationSortedCount,
  selectSeparationStatus,
} from './separation.selectors'

/**
 * Reads the groups separation left unresolved, polling while the stage is still running.
 *
 * Transaction review and the books question both mount this, so the set lives in the store
 * rather than in each caller: two copies would mean two polling loops and two answers to
 * "how many are left".
 */
export const useSeparationQuestions = () => {
  const dispatch = useAppDispatch()
  const questions = useReselector(selectSeparationQuestions)
  const sortedCount = useReselector(selectSeparationSortedCount)
  const counts = useReselector(selectSeparationCounts)
  const deferred = useReselector(selectSeparationDeferred)
  const status = useReselector(selectSeparationStatus)
  const released = useReselector(selectSeparationReleased)
  const loadFailed = useReselector(selectSeparationLoadFailed)
  const pollDelayRef = useRef(POLL_START_MS)

  const load = useCallback(
    () => dispatch(fetchSeparationQuestions()).then((set) => set?.questions),
    [dispatch]
  )

  useEffect(() => {
    void load()
  }, [load])

  // Someone who answered everything but closed the tab before the finish screen never
  // released their answers to autocat, and no scheduled sweep catches that. `complete` is
  // idempotent and publishes nothing when there is nothing left uncategorized.
  useEffect(() => {
    if (status !== 'ready' || questions?.length !== 0 || released || loadFailed)
      return
    dispatch(markSeparationReleased())
    void dispatch(completeSeparation())
  }, [dispatch, questions, released, status, loadFailed])

  // The stage runs after the sync, so a screen opened during onboarding almost always mounts
  // while status is still `pending`. Without this the first fetch is the only one and the
  // waiting state never resolves.
  useEffect(() => {
    if (status !== 'pending' || loadFailed) {
      pollDelayRef.current = POLL_START_MS
      return undefined
    }

    const timer = setTimeout(() => {
      pollDelayRef.current = Math.min(pollDelayRef.current * 2, POLL_MAX_MS)
      void load()
    }, pollDelayRef.current)

    return () => {
      clearTimeout(timer)
    }
  }, [status, questions, load, loadFailed])

  return {
    questions,
    sortedCount,
    counts,
    deferred,
    status,
    loadFailed,
    refetch: load,
  }
}
